import { useState, useRef } from 'react';

import PropTypes from 'prop-types';
import { BsUpload, BsFileEarmarkText, BsX } from 'react-icons/bs';

import Button from '#globalComponents/Button/Button';
import { parseCSV, readFileAsText } from '#lib/csv/csvParser';

export default function FileUploadStep({ onFileLoaded, onNext }) {
	const [isDragging, setIsDragging] = useState(false);
	const [file, setFile] = useState(null);
	const [error, setError] = useState(null);
	const [isLoading, setIsLoading] = useState(false);
	const fileInputRef = useRef(null);

	const handleDragOver = (e) => {
		e.preventDefault();
		setIsDragging(true);
	};

	const handleDragLeave = (e) => {
		e.preventDefault();
		setIsDragging(false);
	};

	const handleDrop = async (e) => {
		e.preventDefault();
		setIsDragging(false);

		const droppedFile = e.dataTransfer.files[0];
		if (droppedFile) {
			await processFile(droppedFile);
		}
	};

	const handleFileSelect = async (e) => {
		const selectedFile = e.target.files[0];
		if (selectedFile) {
			await processFile(selectedFile);
		}
	};

	const processFile = async (selectedFile) => {
		// Validate file type
		if (!selectedFile.name.toLowerCase().endsWith('.csv')) {
			setError('Bitte eine CSV-Datei auswählen');
			return;
		}

		// Validate file size (max 10MB)
		if (selectedFile.size > 10 * 1024 * 1024) {
			setError('Datei ist zu groß (max. 10MB)');
			return;
		}

		setIsLoading(true);
		setError(null);

		try {
			const content = await readFileAsText(selectedFile);
			const { headers, rows, totalRows } = parseCSV(content, 5); // Preview first 5 rows

			if (headers.length === 0) {
				setError('CSV-Datei enthält keine Spalten');
				return;
			}

			if (totalRows === 0) {
				setError('CSV-Datei enthält keine Daten');
				return;
			}

			setFile(selectedFile);

			// Pass all data to parent
			onFileLoaded({
				fileName: selectedFile.name,
				headers,
				previewRows: rows,
				totalRows,
				rawContent: content,
			});
		} catch (err) {
			setError('Fehler beim Lesen der Datei: ' + err.message);
		} finally {
			setIsLoading(false);
		}
	};

	const handleRemoveFile = () => {
		setFile(null);
		setError(null);
		onFileLoaded(null);
		if (fileInputRef.current) {
			fileInputRef.current.value = '';
		}
	};

	const handleClickUpload = () => {
		fileInputRef.current?.click();
	};

	return (
		<div className="space-y-6">
			<div className="text-center">
				<h3 className="text-lg font-medium text-text mb-2">CSV-Datei hochladen</h3>
				<p className="text-sm text-bgBase-800">Laden Sie eine CSV-Datei mit Ihren Kontaktdaten hoch</p>
			</div>

			{/* Upload Area */}
			{!file ? (
				<div
					className={`border-2 border-dashed rounded-lg p-8 text-center transition-colors cursor-pointer ${
						isDragging ? 'border-purple-500 bg-purple-50' : 'border-gray-300 hover:border-gray-400'
					}`}
					onDragOver={handleDragOver}
					onDragLeave={handleDragLeave}
					onDrop={handleDrop}
					onClick={handleClickUpload}
				>
					<input
						ref={fileInputRef}
						type="file"
						accept=".csv"
						onChange={handleFileSelect}
						className="hidden"
					/>

					<BsUpload className={`mx-auto h-12 w-12 ${isDragging ? 'text-primary-500' : 'text-bgBase-800'}`} />

					<p className="mt-4 text-sm text-text">
						<span className="font-medium text-primary-600 hover:text-primary-500">Datei auswählen</span>{' '}
						oder per Drag & Drop ablegen
					</p>
					<p className="mt-1 text-xs text-bgBase-800">CSV-Datei, max. 10MB</p>

					{isLoading && (
						<div className="mt-4 flex items-center justify-center">
							<div className="animate-spin rounded-full h-6 w-6 border-b-2 border-purple-500" />
							<span className="ml-2 text-sm text-bgBase-800">Datei wird geladen...</span>
						</div>
					)}
				</div>
			) : (
				<div className="border border-gray-200 rounded-lg p-4">
					<div className="flex items-center justify-between">
						<div className="flex items-center space-x-3">
							<BsFileEarmarkText className="h-8 w-8 text-primary-500" />
							<div>
								<p className="text-sm font-medium text-text">{file.name}</p>
								<p className="text-xs text-bgBase-800">{(file.size / 1024).toFixed(1)} KB</p>
							</div>
						</div>
						<button onClick={handleRemoveFile} className="p-1 text-bgBase-800 hover:text-text">
							<BsX className="h-5 w-5" />
						</button>
					</div>
				</div>
			)}

			{/* Error Message */}
			{error && (
				<div className="bg-red-50 border border-red-200 rounded-lg p-3">
					<p className="text-sm text-warning-700">{error}</p>
				</div>
			)}

			{/* Next Button */}
			<div className="flex justify-end">
				<Button variant="solid" onClick={onNext} disabled={!file}>
					Weiter
				</Button>
			</div>
		</div>
	);
}

FileUploadStep.propTypes = {
	onFileLoaded: PropTypes.func.isRequired,
	onNext: PropTypes.func.isRequired,
};
